import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

/**
 * Loading states, which the screen-archetypes standard lists under "Not covered by the prototype"
 * and requires each module to design — and which every screen here previously answered with the
 * word "Caricamento…".
 *
 * WHY A SKELETON RATHER THAN A WORD OR A SPINNER. A line of text is a different height from the
 * table that replaces it, so the page jumps and whatever the reader had aimed at moves. A spinner
 * says only that something is happening. A skeleton says WHERE the content will be and roughly how
 * much of it there is, so the eye is already in the right place when it lands, and the layout does
 * not reflow at all. The standard's own acceptance list requires the state to exist; it is the
 * platform's § Feedback rule — "every action produces a visible response within 200 ms" — that
 * makes it worth being this specific.
 *
 * ONE ANNOUNCEMENT, MANY BONES. The bones themselves are decoration and are hidden from the
 * accessibility tree; the wrapper carries `role="status"` and one short message, so a screen reader
 * says "Caricamento…" once instead of enumerating fourteen empty boxes.
 */
export function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return <div aria-hidden="true" className={cn('skeleton', className)} {...props} />;
}

/**
 * The announcement wrapper. Every skeleton on a screen should be inside exactly one of these —
 * `role="status"` is a live region, and three of them would announce three times. `label` replaces
 * the generic «Caricamento…» when the screen can say what is loading.
 */
export function SkeletonRegion({
  className,
  children,
  label,
}: React.ComponentProps<'div'> & { readonly label?: string }) {
  const { t } = useTranslation('common');

  return (
    <div role="status" aria-busy="true" className={className}>
      <span className="sr-only">{label ?? t('loading')}</span>
      {/*
       * THE BONES ARE HIDDEN FROM ASSISTIVE TECHNOLOGY AS A GROUP, not one by one, and that
       * matters most for `TableSkeleton`: it draws a REAL `<table>` so the column widths, the
       * header height and the density-driven row height are the loaded table's own, and a real
       * table is announced as a table — offering a screen reader a grid of empty cells to
       * navigate, and letting any "wait for the table" query resolve against a placeholder that
       * is about to be replaced. The wrapper is `display: contents`, so hiding it costs the
       * layout nothing; the region's own `role="status"` still says "Caricamento…" exactly once.
       */}
      <div aria-hidden="true" style={{ display: 'contents' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * A list's own loading state: the real table chrome with bones where the cells will be.
 *
 * It renders the SAME `Table` primitives as the loaded list, so the header height, the row height
 * (`--density-row-h`, which follows the user's density choice) and the border treatment are the
 * real ones rather than a second approximation that drifts. Column headers are passed in when the
 * caller knows them — they are known before the data arrives, and showing them is free information.
 */
export function TableSkeleton({
  columns,
  rows = 5,
  headers,
}: {
  /** How many columns the loaded table will have. */
  columns: number;
  /** How many bone rows to draw. Five is about a screen's worth without being a wall. */
  rows?: number;
  /** The real column labels, when the caller has them; bones are drawn for the rest. */
  headers?: readonly string[];
}) {
  return (
    <SkeletonRegion>
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: columns }, (_unused, column) => (
              <TableHead key={column}>
                {headers?.[column] ?? <Skeleton className="h-3 w-20" />}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }, (_unused, row) => (
            <TableRow key={row}>
              {Array.from({ length: columns }, (_unused, column) => (
                <TableCell key={column}>
                  {/* Varying widths, derived from the cell's position rather than from
                      `Math.random()`: a deterministic pattern still reads as text of different
                      lengths, and it does not change on every re-render — which is both visually
                      noisy and impossible to snapshot. */}
                  <Skeleton
                    className="h-3.5"
                    style={{ width: `${[72, 54, 88, 46, 64][(row + column) % 5] ?? 64}%` }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SkeletonRegion>
  );
}

/**
 * The statement/dashboard shape: a title bone, then a band of card bones. Used by the Finance
 * views and the study overview, whose loaded state is cards of figures rather than a table.
 */
export function CardsSkeleton({ cards = 4, rows = 3 }: { cards?: number; rows?: number }) {
  return (
    <SkeletonRegion className="space-y-6">
      <Skeleton className="h-5 w-56" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: cards }, (_unused, card) => (
          <div key={card} className="border-border bg-card space-y-3 rounded-md border p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-6 w-32" />
          </div>
        ))}
      </div>
      <div className="border-border bg-card space-y-3 rounded-md border p-4">
        {Array.from({ length: rows }, (_unused, row) => (
          <Skeleton
            key={row}
            className="h-4"
            style={{ width: `${[92, 78, 85][row % 3] ?? 85}%` }}
          />
        ))}
      </div>
    </SkeletonRegion>
  );
}
